/**
 * CustomerSitePage.tsx — Customer Site Data Hub (Enhanced)
 *
 * Per-customer water data repository with full site management:
 *  - Multiple physical sites per customer (create / edit / delete / activate)
 *  - Site-level details: address, permit ref, discharge route, standard override
 *  - All influent & effluent samples (filtered by active site when set)
 *  - Saved study reports and process trains
 *  - Parameter averages, trend charts, compliance overview
 *  - Direct navigation to all study / builder pages
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Building2, Droplets, Beaker, FileText, Workflow, MapPin,
  Plus, RefreshCw, ChevronRight, CheckCircle2,
  TrendingUp, Activity, Database, Users, Shield, ShieldCheck,
  Calendar, ArrowRight, Microscope,
  Trash2, X, Edit3, Save, BarChart3, Package,
  Globe, Phone, Mail, Hash, ClipboardList, AlertTriangle,
  Navigation2, Layers, Star, Info, ChevronDown, ChevronUp, Waves,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { toast } from 'sonner';
import {
  getAllLocalSamples, getLocalProcessTrains,
  getLocalSites, saveLocalSite, updateLocalSite, deleteLocalSite,
  type CustomerSite, type SiteCategory,
} from '@/lib/hybridStorage';
import { apiListInfluentSamples, apiListReports, apiDeleteReport, hasPhpBackend, hasAuthToken, apiSaveCustomerSite } from '@/lib/phpApi';
import { ALL_STANDARDS } from '@/types/standards';

// ─── Types ─────────────────────────────────────────────────────────────────

interface SampleSummary {
  id: number;
  sampleName: string;
  reportNumber?: string | null;
  params: Record<string, number>;
  createdAt: string;
  sampleType: 'influent' | 'effluent';
  source: 'local' | 'server';
  siteId?: string | null;
}

interface StudyReport {
  id: number | string;
  title: string;
  reportType: 'influent_study' | 'effluent_study' | string;
  generatedAt: string;
  complianceScore?: number;
  treatabilityRating?: string;
  source: 'local' | 'server';
}

interface ProcessTrainSummary {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  equipmentList: unknown[];
}

type SiteFormData = {
  name: string;
  address: string;
  postcode: string;
  country: string;
  state: string;
  permitRef: string;
  dischargeRoute: string;
  siteCategory: string;
  standard: string;
  notes: string;
};

const BLANK_FORM: SiteFormData = {
  name: '', address: '', postcode: '', country: 'AU',
  state: '', permitRef: '', dischargeRoute: '', siteCategory: 'treatment_plant', standard: '', notes: '',
};

// ─── Constants ─────────────────────────────────────────────────────────────

const KEY_PARAMS = [
  { key: 'bod5',            fallback: 'bod',   label: 'BOD₅',    unit: 'mg/L', color: '#10b981' },
  { key: 'cod',             fallback: null,    label: 'COD',     unit: 'mg/L', color: '#3b82f6' },
  { key: 'tss',             fallback: null,    label: 'TSS',     unit: 'mg/L', color: '#06b6d4' },
  { key: 'ph',              fallback: null,    label: 'pH',      unit: '',     color: '#f59e0b' },
  { key: 'ammoniaN',        fallback: 'nh3n',  label: 'NH₃-N',   unit: 'mg/L', color: '#a78bfa' },
  { key: 'totalPhosphorus', fallback: 'tp',    label: 'Total P', unit: 'mg/L', color: '#ec4899' },
];

const COUNTRIES = [
  { code: 'AU', label: 'Australia' }, { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' }, { code: 'NZ', label: 'New Zealand' },
  { code: 'CA', label: 'Canada' }, { code: 'IE', label: 'Ireland' },
  { code: 'DE', label: 'Germany' }, { code: 'FR', label: 'France' },
  { code: 'SG', label: 'Singapore' }, { code: 'OTHER', label: 'Other' },
];

const DISCHARGE_ROUTES = [
  'Municipal sewer / trade waste', 'Direct discharge to river',
  'Direct discharge to estuary / coastal', 'Reuse / recycled water scheme',
  'Land application / irrigation', 'Evaporation pond',
  'Reservoir / Lake (closed water body)',
  'Deep well injection', 'Holding tank / tanker removal',
];

const SITE_TYPE_COLORS: Record<string, string> = {
  'Municipal sewer / trade waste':             '#3b82f6',
  'Direct discharge to river':                 '#10b981',
  'Direct discharge to estuary / coastal':     '#06b6d4',
  'Reuse / recycled water scheme':             '#a78bfa',
  'Land application / irrigation':             '#84cc16',
  'Evaporation pond':                          '#f59e0b',
  'Reservoir / Lake (closed water body)':      '#22d3ee',
  'Deep well injection':                       '#f97316',
  'Holding tank / tanker removal':             '#94a3b8',
};

const SITE_CATEGORIES: { value: string; label: string; emoji: string }[] = [
  { value: 'treatment_plant',    label: 'Treatment Plant',       emoji: '🏭' },
  { value: 'reservoir',          label: 'Reservoir',             emoji: '💧' },
  { value: 'lake',               label: 'Natural Lake',          emoji: '🏞️' },
  { value: 'lagoon',             label: 'Lagoon / Pond',         emoji: '🌊' },
  { value: 'constructed_wetland',label: 'Constructed Wetland',   emoji: '🌿' },
  { value: 'pump_station',       label: 'Pump Station',          emoji: '⚙️' },
  { value: 'other',              label: 'Other',                 emoji: '📍' },
];

const RESERVOIR_CATEGORIES = new Set(['reservoir', 'lake', 'lagoon', 'constructed_wetland']);

// ─── Helpers ───────────────────────────────────────────────────────────────

function getParam(params: Record<string, number>, key: string, fallback: string | null): number {
  return params[key] ?? (fallback ? params[fallback] : 0) ?? 0;
}

function formatDate(d: string) {
  try {
    return new Date(d.replace(' ', 'T')).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: '2-digit',
    });
  } catch { return d; }
}

function timeAgo(d: string) {
  try {
    const diff = (Date.now() - new Date(d.replace(' ', 'T')).getTime()) / 1000;
    if (diff < 3600)   return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400)  return `${Math.round(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
    return formatDate(d);
  } catch { return d; }
}

function siteDischargeColor(route?: string) {
  return route ? (SITE_TYPE_COLORS[route] ?? '#94a3b8') : '#94a3b8';
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon: Icon, color = '#3b82f6' }: {
  label: string; value: string | number; sub?: string; icon: React.ElementType; color?: string;
}) {
  return (
    <div className="rb-card p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{label}</div>
        <div className="text-xl font-bold text-slate-100 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function SampleCard({ sample, type, onClick }: {
  sample: SampleSummary; type: 'influent' | 'effluent'; onClick: () => void;
}) {
  const color = type === 'influent' ? '#3b82f6' : '#10b981';
  return (
    <button onClick={onClick}
      className="w-full text-left p-3.5 rounded-xl border border-slate-800/60 bg-rb-elevated
                 hover:border-slate-600 hover:bg-rb-active/30 transition-all group">
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="min-w-0">
          <div className="text-xs font-bold text-slate-200 truncate">{sample.sampleName}</div>
          <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(sample.createdAt)}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {sample.source === 'local' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700/40">
              LOCAL
            </span>
          )}
          <ChevronRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-300 transition-colors" />
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {KEY_PARAMS.slice(0, 4).map(p => {
          const v = getParam(sample.params, p.key, p.fallback);
          return v > 0 ? (
            <span key={p.key} className="text-[10px] font-mono rounded-lg px-1.5 py-0.5 bg-slate-800/80 border border-slate-700/30">
              <span className="text-slate-500">{p.label} </span>
              <span className="font-bold" style={{ color: p.color }}>
                {v > 10 ? Math.round(v) : v.toFixed(2)}
              </span>
            </span>
          ) : null;
        })}
      </div>
    </button>
  );
}

function ReportCard({ report, onDelete }: { report: StudyReport; onDelete: () => void }) {
  const isInfluent = report.reportType === 'influent_study';
  const color = isInfluent ? '#3b82f6' : '#10b981';
  const Icon = isInfluent ? Droplets : Beaker;
  const scoreColor = report.complianceScore !== undefined
    ? (report.complianceScore >= 90 ? '#10b981' : report.complianceScore >= 70 ? '#f59e0b' : '#ef4444')
    : undefined;
  return (
    <div className="p-3.5 rounded-xl border border-slate-800/60 bg-rb-elevated flex items-start gap-3 group
                    hover:border-slate-700 transition-all">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-slate-200 truncate">{report.title}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">
          {isInfluent ? 'Treatability Study' : 'Compliance Study'} · {formatDate(report.generatedAt)}
        </div>
        <div className="flex items-center gap-3 mt-1">
          {report.complianceScore !== undefined && (
            <div className="text-[10px] font-bold" style={{ color: scoreColor }}>
              {report.complianceScore}% Compliant
            </div>
          )}
          {report.treatabilityRating && (
            <div className="text-[10px] text-blue-400 font-bold capitalize">
              {report.treatabilityRating} Treatability
            </div>
          )}
        </div>
      </div>
      <button onClick={onDelete}
        className="p-1.5 text-slate-500 hover:text-red-400
                   hover:bg-red-500/10 rounded-lg transition-all shrink-0 border border-transparent hover:border-red-800/30">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function SiteCard({
  site, sampleCount, isActive, onActivate, onEdit, onDelete, onManageReservoir,
}: {
  site: CustomerSite;
  sampleCount: number;
  isActive: boolean;
  onActivate: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onManageReservoir: () => void;
}) {
  const accentColor = siteDischargeColor(site.dischargeRoute);
  const isReservoir = RESERVOIR_CATEGORIES.has(site.siteCategory ?? '');
  const catMeta = SITE_CATEGORIES.find(c => c.value === site.siteCategory);

  return (
    <div className={`rb-card p-0 overflow-hidden transition-all group ${
      isActive ? 'ring-1 ring-rb-brand/50' : 'hover:border-slate-700'
    }`}>
      {/* Colour accent bar — cyan for reservoir types */}
      <div className="h-1 w-full" style={{ background: isReservoir ? '#22d3ee' : accentColor }} />

      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {catMeta && (
                <span className="text-base leading-none" title={catMeta.label}>{catMeta.emoji}</span>
              )}
              <span className="text-sm font-bold text-slate-100 truncate">{site.name}</span>
              {isActive && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full
                                 bg-rb-brand/15 text-rb-brand border border-rb-brand/30 shrink-0">
                  ACTIVE
                </span>
              )}
              {catMeta && catMeta.value !== 'treatment_plant' && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-800/30 shrink-0">
                  {catMeta.label}
                </span>
              )}
            </div>
            {(site.address || site.postcode || site.country) && (
              <div className="flex items-center gap-1 mt-0.5 text-[11px] text-slate-500">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {[site.address, site.state, site.postcode, site.country].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
          </div>
          {/* Actions */}
          <div className="flex gap-1 shrink-0">
            <button onClick={onEdit}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-rb-elevated
                         border border-slate-700/50 hover:border-slate-600 transition-all">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button onClick={onDelete}
              className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10
                         border border-transparent hover:border-red-800/40 transition-all">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Detail pills */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {site.dischargeRoute && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: `${isReservoir ? '#22d3ee' : accentColor}18`, color: isReservoir ? '#22d3ee' : accentColor, border: `1px solid ${isReservoir ? '#22d3ee' : accentColor}30` }}>
              <Navigation2 className="w-2.5 h-2.5" />
              {site.dischargeRoute}
            </span>
          )}
          {site.standard && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full
                             bg-cyan-500/10 text-cyan-400 border border-cyan-800/30 font-medium">
              <Shield className="w-2.5 h-2.5" />
              {site.standard}
            </span>
          )}
          {site.permitRef && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full
                             bg-slate-800 text-slate-400 border border-slate-700/40 font-medium">
              <Hash className="w-2.5 h-2.5" />
              {site.permitRef}
            </span>
          )}
        </div>

        {/* Sample count + notes */}
        <div className="flex items-center justify-between text-[10px] text-slate-600 mb-3">
          <span className="flex items-center gap-1">
            <Database className="w-3 h-3" />
            {sampleCount} sample{sampleCount !== 1 ? 's' : ''} linked
          </span>
          <span>{formatDate(site.createdAt)}</span>
        </div>

        {site.notes && (
          <p className="text-[10px] text-slate-500 italic mb-3 line-clamp-2 leading-relaxed">
            {site.notes}
          </p>
        )}

        {/* Reservoir management button */}
        {isReservoir && (
          <button onClick={onManageReservoir}
            className="w-full py-1.5 mb-2 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-1.5
                       bg-cyan-500/10 text-cyan-400 border border-cyan-800/40 hover:bg-cyan-500/20 hover:border-cyan-700">
            <Waves className="w-3.5 h-3.5" /> Manage Reservoir / Processes
          </button>
        )}

        {/* Activate button */}
        <button onClick={onActivate}
          className={`w-full py-1.5 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center gap-1.5 ${
            isActive
              ? 'bg-rb-brand/10 text-rb-brand border border-rb-brand/30'
              : 'bg-rb-surface text-slate-400 border border-slate-700/50 hover:bg-rb-elevated hover:text-slate-100 hover:border-slate-600'
          }`}>
          {isActive ? (
            <><CheckCircle2 className="w-3.5 h-3.5" /> Viewing this site</>
          ) : (
            <><Star className="w-3.5 h-3.5" /> Set as Active Site</>
          )}
        </button>
      </div>
    </div>
  );
}

function SiteFormPanel({
  initial, onSave, onCancel, customerId,
}: {
  initial?: CustomerSite | null;
  onSave: (site: CustomerSite) => void;
  onCancel: () => void;
  customerId: string;
}) {
  const { currentCustomer } = useSession();
  const [form, setForm] = useState<SiteFormData>(
    initial
      ? {
          name: initial.name,
          address: initial.address,
          postcode: initial.postcode,
          country: initial.country,
          state: initial.state ?? '',
          permitRef: initial.permitRef ?? '',
          dischargeRoute: initial.dischargeRoute ?? '',
          siteCategory: initial.siteCategory ?? 'treatment_plant',
          standard: initial.standard ?? '',
          notes: initial.notes ?? '',
        }
      : BLANK_FORM,
  );
  const [saving, setSaving] = useState(false);

  function set(field: keyof SiteFormData, value: string) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    if (!form.name.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    try {
      let saved: CustomerSite;
      if (initial) {
        updateLocalSite(initial.id, {
          name: form.name.trim(),
          address: form.address.trim(),
          postcode: form.postcode.trim(),
          country: form.country,
          state: form.state.trim() || undefined,
          permitRef: form.permitRef.trim() || undefined,
          dischargeRoute: form.dischargeRoute || undefined,
          siteCategory: (form.siteCategory || 'treatment_plant') as SiteCategory,
          standard: form.standard || undefined,
          notes: form.notes.trim() || undefined,
        });
        saved = { ...initial, ...form, siteCategory: (form.siteCategory || 'treatment_plant') as SiteCategory, updatedAt: new Date().toISOString() };
        toast.success('Site updated');
      } else {
        // Resolve the active session customer's ID for storage
        const effectiveCustomerId = customerId || currentCustomer?.id || '';
        if (!effectiveCustomerId) {
          toast.error('No active customer session — select a customer first');
          return;
        }
        saved = saveLocalSite({
          customerId: effectiveCustomerId,
          name: form.name.trim(),
          address: form.address.trim(),
          postcode: form.postcode.trim(),
          country: form.country,
          state: form.state.trim() || undefined,
          permitRef: form.permitRef.trim() || undefined,
          dischargeRoute: form.dischargeRoute || undefined,
          siteCategory: (form.siteCategory || 'treatment_plant') as SiteCategory,
          standard: form.standard || undefined,
          notes: form.notes.trim() || undefined,
        });

        // Sync to server when authenticated
        if (hasAuthToken()) {
          const cust = currentCustomer;
          const serverCid = cust
            ? ((cust as any).serverId ?? (cust.id.length < 13 && Number(cust.id) > 0 ? Number(cust.id) : 0))
            : 0;
          if (serverCid > 0) {
            try {
              const res = await apiSaveCustomerSite({
                customerId: serverCid,
                name: saved.name,
                address: saved.address,
                postcode: saved.postcode,
                country: saved.country,
                state: saved.state,
                permitRef: saved.permitRef,
                dischargeRoute: saved.dischargeRoute,
                standard: saved.standard || null,
                notes: saved.notes,
              });
              if (res?.id) {
                updateLocalSite(saved.id, { serverId: res.id, serverCustomerId: serverCid });
                saved = { ...saved, serverId: res.id, serverCustomerId: serverCid };
              }
            } catch {
              toast.warning('Site saved locally — server sync failed. It will retry when online.');
            }
          }
        }

        toast.success(`Site "${saved.name}" created and linked to ${currentCustomer?.name ?? 'active session'}`);
      }
      onSave(saved);
    } finally {
      setSaving(false);
    }
  }

  const inputCls = `w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2
                    text-sm text-slate-200 placeholder:text-slate-600
                    focus:border-slate-500 focus:outline-none transition-colors`;
  const labelCls = 'block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1';

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.2 }}
      className="rb-card p-5 border-rb-brand/20 bg-rb-surface mb-4"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-rb-brand/10 border border-rb-brand/25 flex items-center justify-center">
            <Building2 className="w-3.5 h-3.5 text-rb-brand" />
          </div>
          <h3 className="text-sm font-bold text-slate-200">
            {initial ? 'Edit Site' : 'Add New Site'}
          </h3>
        </div>
        <button onClick={onCancel}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-rb-elevated transition-all">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Site Name */}
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Site Name <span className="text-rb-brand">*</span></label>
          <input className={inputCls} placeholder="e.g. Main Plant, Site B, North Facility"
            value={form.name} onChange={e => set('name', e.target.value)} />
        </div>

        {/* Address */}
        <div className="sm:col-span-2">
          <label className={labelCls}>Street Address</label>
          <input className={inputCls} placeholder="123 Industrial Ave"
            value={form.address} onChange={e => set('address', e.target.value)} />
        </div>

        {/* Postcode */}
        <div>
          <label className={labelCls}>Postcode / ZIP</label>
          <input className={inputCls} placeholder="2000"
            value={form.postcode} onChange={e => set('postcode', e.target.value)} />
        </div>

        {/* Country */}
        <div>
          <label className={labelCls}>Country</label>
          <select className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}>
            {COUNTRIES.map(c => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* State / Region */}
        <div>
          <label className={labelCls}>State / Region</label>
          <input className={inputCls} placeholder="NSW, VIC, England…"
            value={form.state} onChange={e => set('state', e.target.value)} />
        </div>

        {/* Permit Ref */}
        <div>
          <label className={labelCls}>Permit / Licence Ref</label>
          <input className={inputCls} placeholder="EP-12345"
            value={form.permitRef} onChange={e => set('permitRef', e.target.value)} />
        </div>

        {/* Site Category */}
        <div>
          <label className={labelCls}>Site Type</label>
          <select className={inputCls} value={form.siteCategory} onChange={e => {
            set('siteCategory', e.target.value);
            // Auto-set discharge route for reservoir/lake types
            if (['reservoir','lake','lagoon'].includes(e.target.value) && !form.dischargeRoute) {
              set('dischargeRoute', 'Reservoir / Lake (closed water body)');
            }
          }}>
            {SITE_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>
            ))}
          </select>
        </div>

        {/* Discharge Route */}
        <div className="sm:col-span-2">
          <label className={labelCls}>Discharge Route</label>
          <select className={inputCls} value={form.dischargeRoute} onChange={e => set('dischargeRoute', e.target.value)}>
            <option value="">— Select discharge route —</option>
            {DISCHARGE_ROUTES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Standard Override */}
        <div>
          <label className={labelCls}>Standard Override</label>
          <select className={inputCls} value={form.standard} onChange={e => set('standard', e.target.value)}>
            <option value="">— Inherit from customer —</option>
            {ALL_STANDARDS.map(s => (
              <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Notes</label>
          <textarea className={`${inputCls} resize-none`} rows={2}
            placeholder="Process description, operational notes, seasonal constraints…"
            value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-800/50">
        <button onClick={onCancel}
          className="px-4 py-2 text-xs font-bold text-slate-400 hover:text-slate-200
                     bg-rb-elevated border border-slate-700/60 rounded-xl transition-colors">
          Cancel
        </button>
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 text-xs font-bold text-black bg-rb-brand hover:bg-yellow-400
                     rounded-xl transition-colors flex items-center gap-1.5 disabled:opacity-60">
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Create Site'}
        </button>
      </div>
    </motion.div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const ACTIVE_SITE_KEY = (cid: string) => `rb_active_site_${cid}`;

export default function CustomerSitePage() {
  const navigate = useNavigate();
  const { currentCustomer, activeStandard } = useSession();

  const [influentSamples, setInfluentSamples] = useState<SampleSummary[]>([]);
  const [effluentSamples, setEffluentSamples] = useState<SampleSummary[]>([]);
  const [studies, setStudies]   = useState<StudyReport[]>([]);
  const [trains, setTrains]     = useState<ProcessTrainSummary[]>([]);
  const [sites, setSites]       = useState<CustomerSite[]>([]);
  const [loading, setLoading]   = useState(false);
  const [loaded, setLoaded]     = useState(false);

  const [activeView, setActiveView] = useState<
    'overview' | 'influent' | 'effluent' | 'reports' | 'trains' | 'sites'
  >('overview');

  const [activeSiteId, setActiveSiteId] = useState<string | null>(null);
  const [siteFormOpen, setSiteFormOpen] = useState(false);
  const [editingSite, setEditingSite]   = useState<CustomerSite | null>(null);

  const standardName = ALL_STANDARDS.find(s => s.code === activeStandard)?.name ?? activeStandard ?? '';

  // ─── Persist active site per customer ──────────────────────────────────
  useEffect(() => {
    if (!currentCustomer) return;
    const stored = localStorage.getItem(ACTIVE_SITE_KEY(currentCustomer.id));
    setActiveSiteId(stored ?? null);
  }, [currentCustomer]);

  function setAndPersistActiveSite(id: string | null) {
    setActiveSiteId(id);
    if (!currentCustomer) return;
    if (id) localStorage.setItem(ACTIVE_SITE_KEY(currentCustomer.id), id);
    else     localStorage.removeItem(ACTIVE_SITE_KEY(currentCustomer.id));
  }

  // ─── Load all data ──────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // ── Samples from local storage ──
      const local = getAllLocalSamples();
      const localInf = local.filter(s => s.sampleType === 'influent' || !s.sampleType)
        .map((s): SampleSummary => ({
          id: s.id, sampleName: s.sampleName, reportNumber: s.reportNumber,
          params: s.params, createdAt: s.createdAt, sampleType: 'influent',
          source: 'local', siteId: s.siteId,
        }));
      const localEff = local.filter(s => s.sampleType === 'effluent')
        .map((s): SampleSummary => ({
          id: s.id, sampleName: s.sampleName, reportNumber: s.reportNumber,
          params: s.params, createdAt: s.createdAt, sampleType: 'effluent',
          source: 'local', siteId: s.siteId,
        }));

      let serverInf: SampleSummary[] = [];
      let serverEff: SampleSummary[] = [];
      let serverReports: StudyReport[] = [];

      if (hasPhpBackend()) {
        try {
          const [infRes, effRes, rptRes] = await Promise.all([
            apiListInfluentSamples(currentCustomer?.serverId, { type: 'influent' }),
            apiListInfluentSamples(currentCustomer?.serverId, { type: 'effluent' }),
            apiListReports(),
          ]);
          const toSummary = (s: Record<string, unknown>, type: 'influent' | 'effluent'): SampleSummary => ({
            id: s.id as number,
            sampleName: (s.sampleName ?? s.sample_name ?? 'Sample') as string,
            reportNumber: (s.reportNumber ?? s.report_number ?? null) as string | null,
            params: (s.params ?? {}) as Record<string, number>,
            createdAt: (s.createdAt ?? s.created_at ?? '') as string,
            sampleType: type, source: 'server', siteId: (s.siteId ?? null) as string | null,
          });
          serverInf = (infRes ?? []).map((s: Record<string, unknown>) => toSummary(s, 'influent'));
          serverEff = (effRes ?? []).map((s: Record<string, unknown>) => toSummary(s, 'effluent'));
          serverReports = ((rptRes ?? []) as Record<string, unknown>[])
            .filter(r => r.reportType === 'influent_study' || r.reportType === 'effluent_study')
            .map((r): StudyReport => ({
              id: r.id as number,
              title: (r.title ?? 'Study Report') as string,
              reportType: r.reportType as string,
              generatedAt: (r.createdAt ?? r.generated_at ?? '') as string,
              complianceScore: r.complianceScore as number | undefined,
              treatabilityRating: r.treatabilityRating as string | undefined,
              source: 'server',
            }));
        } catch { /* server unavailable */ }
      }

      // ── Local study reports ──
      const localStudies: StudyReport[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) ?? '';
        if (k.startsWith('rb_influent_study_') || k.startsWith('rb_effluent_study_')) {
          try {
            const data = JSON.parse(localStorage.getItem(k) ?? '{}');
            localStudies.push({
              id: k, title: data.title ?? k,
              reportType: k.includes('influent') ? 'influent_study' : 'effluent_study',
              generatedAt: data.savedAt ?? '',
              complianceScore: data.complianceScore,
              treatabilityRating: data.treatabilityRating,
              source: 'local',
            });
          } catch { /* skip */ }
        }
      }

      // ── Process trains ──
      const localTrains = getLocalProcessTrains().map((t): ProcessTrainSummary => ({
        id: t.id, name: t.name, status: t.status, createdAt: t.createdAt, equipmentList: t.equipmentList,
      }));

      // ── Sites ──
      const localSites = currentCustomer
        ? getLocalSites(currentCustomer.id)
        : getLocalSites();

      const dedup = <T extends { id: number | string }>(arr: T[]) => {
        const seen = new Set();
        return arr.filter(s => !seen.has(s.id) && seen.add(s.id));
      };
      const sort = <T extends { createdAt: string }>(arr: T[]) =>
        arr.sort((a, b) =>
          new Date(b.createdAt.replace(' ', 'T')).getTime() -
          new Date(a.createdAt.replace(' ', 'T')).getTime());

      setInfluentSamples(sort(dedup([...serverInf, ...localInf])));
      setEffluentSamples(sort(dedup([...serverEff, ...localEff])));
      setStudies(sort(dedup([...serverReports, ...localStudies])));
      setTrains(sort(localTrains));
      setSites(localSites.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [currentCustomer]);

  useEffect(() => { if (!loaded) loadData(); }, [loaded, loadData]);

  // ─── Derived: filter by active site ────────────────────────────────────
  const activeSite = useMemo(() => sites.find(s => s.id === activeSiteId) ?? null, [sites, activeSiteId]);

  const displayedInf = useMemo(() =>
    activeSiteId
      ? influentSamples.filter(s => !s.siteId || s.siteId === activeSiteId)
      : influentSamples,
  [influentSamples, activeSiteId]);

  const displayedEff = useMemo(() =>
    activeSiteId
      ? effluentSamples.filter(s => !s.siteId || s.siteId === activeSiteId)
      : effluentSamples,
  [effluentSamples, activeSiteId]);

  // ─── Averages ───────────────────────────────────────────────────────────
  const influentAverages = useMemo(() => {
    if (displayedInf.length === 0) return {};
    return KEY_PARAMS.reduce((acc, p) => {
      const vals = displayedInf.map(s => getParam(s.params, p.key, p.fallback)).filter(v => v > 0);
      acc[p.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return acc;
    }, {} as Record<string, number>);
  }, [displayedInf]);

  const effluentAverages = useMemo(() => {
    if (displayedEff.length === 0) return {};
    return KEY_PARAMS.reduce((acc, p) => {
      const vals = displayedEff.map(s => getParam(s.params, p.key, p.fallback)).filter(v => v > 0);
      acc[p.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return acc;
    }, {} as Record<string, number>);
  }, [displayedEff]);

  const timelineData = useMemo(() =>
    displayedInf.slice(0, 8).reverse().map(s => ({
      name: formatDate(s.createdAt),
      BOD: getParam(s.params, 'bod5', 'bod'),
      COD: getParam(s.params, 'cod', null),
      TSS: getParam(s.params, 'tss', null),
    })),
  [displayedInf]);

  const comparisonData = useMemo(() =>
    KEY_PARAMS.slice(0, 5).map(p => ({
      name: p.label,
      Influent: Math.round(influentAverages[p.key] ?? 0),
      Effluent: Math.round(effluentAverages[p.key] ?? 0),
      color: p.color,
    })).filter(d => d.Influent > 0 || d.Effluent > 0),
  [influentAverages, effluentAverages]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleDeleteReport = async (report: StudyReport) => {
    try {
      if (report.source === 'server' && hasPhpBackend()) await apiDeleteReport(report.id as number);
      else localStorage.removeItem(report.id as string);
      setStudies(prev => prev.filter(r => r.id !== report.id));
      toast.success('Report deleted');
    } catch { toast.error('Failed to delete report'); }
  };

  const handleSiteSaved = (saved: CustomerSite) => {
    setSites(prev => {
      const existing = prev.find(s => s.id === saved.id);
      if (existing) return prev.map(s => s.id === saved.id ? saved : s);
      return [saved, ...prev];
    });
    setSiteFormOpen(false);
    setEditingSite(null);
  };

  const handleDeleteSite = (id: string) => {
    if (!confirm('Delete this site? This cannot be undone.')) return;
    deleteLocalSite(id);
    setSites(prev => prev.filter(s => s.id !== id));
    if (activeSiteId === id) setAndPersistActiveSite(null);
    toast.success('Site deleted');
  };

  const handleEditSite = (site: CustomerSite) => {
    setEditingSite(site);
    setSiteFormOpen(true);
  };

  // ─── Sample count per site ──────────────────────────────────────────────
  const sampleCountForSite = useCallback((siteId: string) => {
    const all = [...influentSamples, ...effluentSamples];
    return all.filter(s => s.siteId === siteId).length;
  }, [influentSamples, effluentSamples]);

  // ─── Views ─────────────────────────────────────────────────────────────
  const views = [
    { id: 'overview', label: 'Overview',                             short: 'Overview', icon: BarChart3,  color: '#cda42e' },
    { id: 'influent', label: `Influent (${displayedInf.length})`,    short: 'Influent', icon: Droplets,   color: '#3b82f6' },
    { id: 'effluent', label: `Effluent (${displayedEff.length})`,    short: 'Effluent', icon: Beaker,     color: '#10b981' },
    { id: 'reports',  label: `Studies (${studies.length})`,          short: 'Studies',  icon: FileText,   color: '#f59e0b' },
    { id: 'trains',   label: `Trains (${trains.length})`,            short: 'Trains',   icon: Workflow,   color: '#a855f7' },
    { id: 'sites',    label: `Sites (${sites.length})`,              short: 'Sites',    icon: Building2,  color: '#06b6d4' },
  ] as const;

  const hasData = displayedInf.length > 0 || displayedEff.length > 0;

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">

      {/* Header */}
      <PageHeader
        icon={Building2}
        title={currentCustomer ? currentCustomer.name : 'Customer Site Data Hub'}
        subtitle={currentCustomer
          ? `${currentCustomer.company || 'Site'} · ${currentCustomer.address || 'No address'} · ${standardName}`
          : 'Select a customer from the top bar to view their site data'}
        iconColor="text-amber-400"
        badge={currentCustomer && (
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold
                           bg-amber-500/10 text-amber-400 border border-amber-800/40">
            {currentCustomer.industryType ?? 'Industrial'}
          </span>
        )}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={loadData} disabled={loading}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated
                         border border-slate-700/60 hover:border-slate-500 hover:text-slate-100 rounded-lg transition-colors
                         flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={() => navigate('/compliance-dashboard')}
              className="px-3 py-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10
                         hover:bg-emerald-500/20 border border-emerald-800/40 rounded-lg transition-colors
                         flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Compliance
            </button>
            <button onClick={() => navigate('/influent-study')}
              className="px-3 py-1.5 text-xs font-bold text-blue-400 bg-blue-500/10
                         hover:bg-blue-500/20 border border-blue-800/40 rounded-lg transition-colors
                         flex items-center gap-1.5">
              <Microscope className="w-3.5 h-3.5" /> Influent Study
            </button>
            <button onClick={() => navigate('/effluent-study')}
              className="px-3 py-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10
                         hover:bg-emerald-500/20 border border-emerald-800/40 rounded-lg transition-colors
                         flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Effluent Study
            </button>
          </div>
        }
      />

      {/* No customer */}
      {!currentCustomer && (
        <div className="rb-card p-10 text-center">
          <Building2 className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <div className="text-base font-bold text-slate-400 mb-1">No Customer Selected</div>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Select a customer from the customer bar at the top of the screen to view their site data hub.
          </p>
          <button onClick={() => navigate('/customers')}
            className="mt-5 px-4 py-2 text-sm font-bold text-black bg-rb-brand
                       hover:bg-yellow-400 rounded-xl transition-colors inline-flex items-center gap-2">
            <Users className="w-4 h-4" /> Manage Customers
          </button>
        </div>
      )}

      {currentCustomer && (
        <>
          {/* Active Site Banner */}
          <AnimatePresence>
            {activeSite && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="flex items-center gap-3 px-4 py-2.5 rounded-xl
                           bg-rb-brand/8 border border-rb-brand/25"
              >
                <div className="w-6 h-6 rounded-lg bg-rb-brand/15 border border-rb-brand/30
                               flex items-center justify-center shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-rb-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-bold text-rb-brand">{activeSite.name}</span>
                  {activeSite.address && (
                    <span className="text-xs text-slate-500 ml-2">
                      {[activeSite.address, activeSite.state, activeSite.postcode].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {activeSite.permitRef && (
                    <span className="text-[10px] text-slate-600 ml-2">· Permit: {activeSite.permitRef}</span>
                  )}
                </div>
                <span className="text-[9px] font-bold text-slate-600 hidden sm:block">
                  ACTIVE SITE — data filtered to this location
                </span>
                <button onClick={() => setAndPersistActiveSite(null)}
                  className="p-1 rounded-lg text-slate-500 hover:text-slate-200
                             hover:bg-rb-elevated transition-all shrink-0"
                  title="Clear active site">
                  <X className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Sites"            value={sites.length}         sub={sites.length > 0 ? `${sites.filter(s => s.permitRef).length} with permit` : 'None added'} icon={Building2}  color="#cda42e" />
            <StatCard label="Influent Samples" value={displayedInf.length}  sub={displayedInf.length > 0 ? `Last: ${timeAgo(displayedInf[0].createdAt)}` : 'None stored'} icon={Droplets}   color="#3b82f6" />
            <StatCard label="Effluent Samples" value={displayedEff.length}  sub={displayedEff.length > 0 ? `Last: ${timeAgo(displayedEff[0].createdAt)}` : 'None stored'} icon={Beaker}     color="#10b981" />
            <StatCard label="Study Reports"    value={studies.length}       sub="Saved analyses"          icon={FileText}   color="#f59e0b" />
            <StatCard label="Process Trains"   value={trains.length}        sub="Saved configurations"    icon={Workflow}   color="#a78bfa" />
            <StatCard label="Active Standard"  value={activeStandard ?? '—'} sub={standardName}           icon={Shield}     color="#06b6d4" />
          </div>

          {/* Navigation Tabs — coloured pills, shadow + slow pulse on active (matches InfluentStudyPage) */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {views.map(v => {
              const Icon = v.icon;
              const active = activeView === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setActiveView(v.id)}
                  title={v.label}
                  className="relative flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-3 py-2.5 rounded-xl text-[11px] sm:text-xs font-bold uppercase tracking-wide transition-all border-2 overflow-hidden"
                  style={{
                    background: active
                      ? `linear-gradient(135deg, ${v.color}38, ${v.color}12)`
                      : `linear-gradient(135deg, ${v.color}14, ${v.color}06)`,
                    borderColor: active ? v.color : `${v.color}55`,
                    color: active ? '#f8fafc' : v.color,
                    boxShadow: active
                      ? `0 4px 14px ${v.color}55, 0 0 0 1px ${v.color}88 inset, 0 0 22px ${v.color}44`
                      : `0 2px 6px ${v.color}25, 0 0 0 1px ${v.color}33 inset`,
                  }}
                >
                  {active && (
                    <span
                      className="absolute inset-0 rounded-xl pointer-events-none"
                      style={{ animation: 'rb-tab-pulse 2.6s ease-in-out infinite' }}
                    />
                  )}
                  <Icon
                    className="w-4 h-4 shrink-0"
                    style={{ filter: active ? `drop-shadow(0 0 4px ${v.color}cc)` : 'none' }}
                  />
                  <span className="leading-tight text-center sm:text-left">{v.short}</span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={activeView}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>

              {/* ── OVERVIEW ───────────────────────────────────────────── */}
              {activeView === 'overview' && (
                <div className="space-y-4">
                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    {[
                      { label: 'Influent Treatability Study', desc: 'Analyse water origin & treatment pathway',        icon: Microscope,  color: '#3b82f6', path: '/influent-study' },
                      { label: 'Effluent Compliance Study',   desc: 'Assess discharge compliance & risk',             icon: ShieldCheck,  color: '#10b981', path: '/effluent-study' },
                      { label: 'Process Train Builder',       desc: 'Design & save treatment sequences',              icon: Workflow,     color: '#a78bfa', path: '/process-train' },
                      { label: 'Manage Sites',                desc: 'Add or edit physical site locations',            icon: Building2,    color: '#cda42e', action: () => setActiveView('sites') },
                      { label: 'Compliance Dashboard',        desc: 'Real-time multi-jurisdiction validation',        icon: Shield,       color: '#06b6d4', path: '/compliance-dashboard' },
                    ].map((action, i) => {
                      const Icon = action.icon;
                      return (
                        <button key={i}
                          onClick={'path' in action ? () => navigate(action.path) : action.action}
                          className="rb-card p-4 text-left hover:border-slate-700 transition-all group">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3"
                            style={{ background: `${action.color}18`, border: `1px solid ${action.color}30` }}>
                            <Icon className="w-4.5 h-4.5 group-hover:scale-110 transition-transform"
                              style={{ color: action.color }} />
                          </div>
                          <div className="text-sm font-bold text-slate-200 mb-1 leading-tight">{action.label}</div>
                          <div className="text-[11px] text-slate-500">{action.desc}</div>
                          <div className="flex items-center gap-1 mt-3 text-[10px] font-bold"
                            style={{ color: action.color }}>
                            Open <ArrowRight className="w-3 h-3" />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Sites summary strip (if sites exist) */}
                  {sites.length > 0 && (
                    <div className="rb-card overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-amber-400" />
                          <h3 className="text-sm font-bold text-slate-200">Site Locations</h3>
                        </div>
                        <button onClick={() => setActiveView('sites')}
                          className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1">
                          Manage <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {sites.slice(0, 6).map(site => {
                          const isActive = site.id === activeSiteId;
                          const ac = siteDischargeColor(site.dischargeRoute);
                          return (
                            <button key={site.id}
                              onClick={() => setAndPersistActiveSite(isActive ? null : site.id)}
                              className={`text-left p-3 rounded-xl border transition-all ${
                                isActive
                                  ? 'bg-rb-brand/8 border-rb-brand/25'
                                  : 'bg-rb-elevated border-slate-800/50 hover:border-slate-700'
                              }`}>
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: ac }} />
                                <span className="text-xs font-bold text-slate-200 truncate">{site.name}</span>
                                {isActive && <CheckCircle2 className="w-3.5 h-3.5 text-rb-brand shrink-0" />}
                              </div>
                              <div className="text-[10px] text-slate-500 truncate pl-4">
                                {[site.address, site.state, site.country].filter(Boolean).join(', ')}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Compliance Gateway Banner */}
                  {(displayedEff.length > 0 || displayedInf.length > 0) && (
                    <div className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-gradient-to-r from-emerald-500/8 to-cyan-500/8 border border-emerald-800/25">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-800/30 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-200">Real-Time Compliance Validation</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Validate {displayedEff.length} effluent samples against UK EA · AU EPA · SA Water · EU UWWTD · US EPA simultaneously
                        </div>
                      </div>
                      <button onClick={() => navigate('/compliance-dashboard')}
                        className="px-4 py-2 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors flex items-center gap-1.5 shrink-0 shadow-sm">
                        <ShieldCheck className="w-3.5 h-3.5" /> Open Dashboard
                      </button>
                    </div>
                  )}

                  {!hasData && (
                    <div className="rb-card p-8 text-center">
                      <Database className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                      <div className="text-sm font-bold text-slate-400 mb-1">No Samples Recorded Yet</div>
                      <p className="text-xs text-slate-500">Start by adding an influent sample via Site Assessment or the Influent Entry page.</p>
                    </div>
                  )}

                  {hasData && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Influent Timeline */}
                      {timelineData.length >= 2 && (
                        <div className="rb-card overflow-hidden">
                          <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-blue-400" />
                            <h3 className="text-sm font-bold text-slate-200">Influent Trend (BOD / COD / TSS)</h3>
                          </div>
                          <div className="p-4">
                            <ResponsiveContainer width="100%" height={200}>
                              <LineChart data={timelineData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} />
                                <Line type="monotone" dataKey="BOD" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="COD" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="TSS" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Influent vs Effluent comparison */}
                      {comparisonData.length > 0 && displayedEff.length > 0 && (
                        <div className="rb-card overflow-hidden">
                          <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center gap-2">
                            <Activity className="w-4 h-4 text-amber-400" />
                            <h3 className="text-sm font-bold text-slate-200">Avg Influent vs Effluent</h3>
                          </div>
                          <div className="p-4">
                            <ResponsiveContainer width="100%" height={200}>
                              <BarChart data={comparisonData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(100,116,139,0.1)" />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 11 }} />
                                <Bar dataKey="Influent" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="Effluent" fill="#10b981" radius={[3, 3, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Parameter Averages */}
                      <div className="rb-card overflow-hidden">
                        <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-purple-400" />
                          <h3 className="text-sm font-bold text-slate-200">Average Parameter Concentrations</h3>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-2">
                          {KEY_PARAMS.map(p => {
                            const infAvg = influentAverages[p.key] ?? 0;
                            const effAvg = effluentAverages[p.key] ?? 0;
                            const hasEff = displayedEff.length > 0;
                            return infAvg > 0 ? (
                              <div key={p.key} className="p-2.5 rounded-xl bg-rb-elevated border border-slate-800/40">
                                <div className="text-[10px] text-slate-500 font-bold uppercase">{p.label}</div>
                                <div className="flex items-end justify-between mt-1">
                                  <div>
                                    <div className="text-sm font-bold font-mono" style={{ color: p.color }}>
                                      {infAvg > 10 ? Math.round(infAvg) : infAvg.toFixed(2)}
                                      <span className="text-[10px] text-slate-600 ml-0.5">{p.unit}</span>
                                    </div>
                                    <div className="text-[9px] text-slate-600">Influent avg</div>
                                  </div>
                                  {hasEff && effAvg > 0 && (
                                    <div className="text-right">
                                      <div className="text-xs font-bold font-mono text-emerald-400">
                                        {effAvg > 10 ? Math.round(effAvg) : effAvg.toFixed(2)}
                                        <span className="text-[10px] text-slate-600 ml-0.5">{p.unit}</span>
                                      </div>
                                      <div className="text-[9px] text-slate-600">Effluent avg</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>

                      {/* Recent studies */}
                      {studies.length > 0 && (
                        <div className="rb-card overflow-hidden">
                          <div className="px-5 py-3.5 border-b border-slate-800/50 bg-rb-elevated/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-amber-400" />
                              <h3 className="text-sm font-bold text-slate-200">Recent Studies</h3>
                            </div>
                            <button onClick={() => setActiveView('reports')}
                              className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors">
                              View all
                            </button>
                          </div>
                          <div className="p-4 space-y-2">
                            {studies.slice(0, 3).map(r => (
                              <ReportCard key={r.id} report={r} onDelete={() => handleDeleteReport(r)} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── INFLUENT SAMPLES ───────────────────────────────────── */}
              {activeView === 'influent' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-blue-400" />
                      Influent Sample Repository
                      {activeSite && <span className="text-[10px] text-slate-500 font-normal">· {activeSite.name}</span>}
                    </h3>
                    <div className="flex gap-2">
                      <button onClick={() => navigate('/site-assessment')}
                        className="px-3 py-1.5 text-xs font-bold text-black bg-rb-brand
                                   hover:bg-yellow-400 rounded-xl transition-colors flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" /> Add Sample
                      </button>
                      <button onClick={() => navigate('/influent-study')}
                        className="px-3 py-1.5 text-xs font-bold text-blue-400 bg-blue-500/10
                                   hover:bg-blue-500/20 border border-blue-800/40 rounded-xl transition-colors
                                   flex items-center gap-1.5">
                        <Microscope className="w-3.5 h-3.5" /> Run Study
                      </button>
                    </div>
                  </div>
                  {displayedInf.length === 0 ? (
                    <div className="rb-card p-10 text-center">
                      <Droplets className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                      <div className="text-sm font-bold text-slate-400">No influent samples</div>
                      <p className="text-xs text-slate-500 mt-1">Add samples via Site Assessment or Influent Entry.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {displayedInf.map(s => (
                        <SampleCard key={s.id} sample={s} type="influent"
                          onClick={() => navigate('/influent-study')} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── EFFLUENT SAMPLES ───────────────────────────────────── */}
              {activeView === 'effluent' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                      <Beaker className="w-4 h-4 text-emerald-400" />
                      Effluent Sample Repository
                      {activeSite && <span className="text-[10px] text-slate-500 font-normal">· {activeSite.name}</span>}
                    </h3>
                    <div className="flex gap-2">
                      <button onClick={() => navigate('/effluent-entry')}
                        className="px-3 py-1.5 text-xs font-bold text-black bg-rb-brand
                                   hover:bg-yellow-400 rounded-xl transition-colors flex items-center gap-1.5">
                        <Plus className="w-3.5 h-3.5" /> Add Sample
                      </button>
                      <button onClick={() => navigate('/effluent-study')}
                        className="px-3 py-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10
                                   hover:bg-emerald-500/20 border border-emerald-800/40 rounded-xl transition-colors
                                   flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" /> Compliance Study
                      </button>
                    </div>
                  </div>
                  {displayedEff.length === 0 ? (
                    <div className="rb-card p-10 text-center">
                      <Beaker className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                      <div className="text-sm font-bold text-slate-400">No effluent samples</div>
                      <p className="text-xs text-slate-500 mt-1">Add samples via Effluent Entry.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {displayedEff.map(s => (
                        <SampleCard key={s.id} sample={s} type="effluent"
                          onClick={() => navigate('/effluent-study')} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── STUDY REPORTS ─────────────────────────────────────── */}
              {activeView === 'reports' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-400" />
                      Saved Study Reports
                    </h3>
                    <div className="flex gap-2">
                      <button onClick={() => navigate('/influent-study')}
                        className="px-3 py-1.5 text-xs font-bold text-blue-400 bg-blue-500/10
                                   border border-blue-800/40 rounded-xl transition-colors flex items-center gap-1.5">
                        <Microscope className="w-3.5 h-3.5" /> New Influent Study
                      </button>
                      <button onClick={() => navigate('/effluent-study')}
                        className="px-3 py-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10
                                   border border-emerald-800/40 rounded-xl transition-colors flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" /> New Effluent Study
                      </button>
                    </div>
                  </div>
                  {studies.length === 0 ? (
                    <div className="rb-card p-10 text-center">
                      <FileText className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                      <div className="text-sm font-bold text-slate-400">No saved studies</div>
                      <p className="text-xs text-slate-500 mt-1">Run an influent or effluent study and save the report.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {studies.map(r => (
                        <ReportCard key={r.id} report={r} onDelete={() => handleDeleteReport(r)} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── PROCESS TRAINS ────────────────────────────────────── */}
              {activeView === 'trains' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-300 flex items-center gap-2">
                      <Workflow className="w-4 h-4 text-purple-400" />
                      Saved Process Trains
                    </h3>
                    <button onClick={() => navigate('/process-train')}
                      className="px-3 py-1.5 text-xs font-bold text-black bg-rb-brand
                                 hover:bg-yellow-400 rounded-xl transition-colors flex items-center gap-1.5">
                      <Plus className="w-3.5 h-3.5" /> New Process Train
                    </button>
                  </div>
                  {trains.length === 0 ? (
                    <div className="rb-card p-10 text-center">
                      <Workflow className="w-10 h-10 text-slate-700 mx-auto mb-2" />
                      <div className="text-sm font-bold text-slate-400">No process trains saved</div>
                      <p className="text-xs text-slate-500 mt-1">
                        Build a treatment sequence in the Process Train builder and save it here.
                      </p>
                      <button onClick={() => navigate('/process-train')}
                        className="mt-4 px-4 py-2 text-xs font-bold text-black bg-rb-brand
                                   hover:bg-yellow-400 rounded-xl transition-colors inline-flex items-center gap-2">
                        <Workflow className="w-3.5 h-3.5" /> Open Process Train Builder
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {trains.map(t => (
                        <button key={t.id} onClick={() => navigate('/process-train')}
                          className="rb-card p-4 text-left hover:border-slate-700 transition-all group">
                          <div className="flex items-center gap-2 mb-2">
                            <Workflow className="w-4 h-4 text-purple-400 shrink-0" />
                            <span className="text-sm font-bold text-slate-200 truncate">{t.name}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />{formatDate(t.createdAt)}
                          </div>
                          <div className="flex items-center gap-2 mt-2.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${
                              t.status === 'assessed'    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-800/40' :
                              t.status === 'in_progress' ? 'bg-amber-500/10  text-amber-400  border border-amber-800/40'  :
                                                           'bg-slate-800      text-slate-500  border border-slate-700/40'
                            }`}>{t.status.replace('_', ' ')}</span>
                            <span className="text-[10px] text-slate-600">
                              {(t.equipmentList ?? []).length} stages
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── SITES ─────────────────────────────────────────────── */}
              {activeView === 'sites' && (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-amber-400" />
                        Site Management
                      </h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Add and manage physical treatment locations for {currentCustomer.name}
                      </p>
                    </div>
                    <button
                      onClick={() => { setEditingSite(null); setSiteFormOpen(v => !v); }}
                      className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 ${
                        siteFormOpen && !editingSite
                          ? 'text-slate-300 bg-rb-elevated border border-slate-700'
                          : 'text-black bg-rb-brand hover:bg-yellow-400'
                      }`}>
                      {siteFormOpen && !editingSite ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                      {siteFormOpen && !editingSite ? 'Cancel' : 'Add Site'}
                    </button>
                  </div>

                  {/* Add/Edit Form */}
                  <AnimatePresence>
                    {siteFormOpen && (
                      <SiteFormPanel
                        key={editingSite?.id ?? 'new'}
                        initial={editingSite}
                        customerId={currentCustomer.id}
                        onSave={handleSiteSaved}
                        onCancel={() => { setSiteFormOpen(false); setEditingSite(null); }}
                      />
                    )}
                  </AnimatePresence>

                  {/* Discharge route legend */}
                  {sites.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(SITE_TYPE_COLORS).map(([label, color]) => {
                        const used = sites.some(s => s.dischargeRoute === label);
                        if (!used) return null;
                        return (
                          <span key={label} className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full"
                            style={{ background: `${color}14`, color, border: `1px solid ${color}28` }}>
                            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: color }} />
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Sites grid */}
                  {sites.length === 0 && !siteFormOpen ? (
                    <div className="rb-card p-12 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-amber-500/8 border border-amber-800/25
                                      flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-8 h-8 text-amber-600" />
                      </div>
                      <div className="text-sm font-bold text-slate-400 mb-1">No Sites Added Yet</div>
                      <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                        Add physical site locations to organise samples and treatment trains
                        by facility. Each site can have its own permit reference and discharge standard.
                      </p>
                      <button
                        onClick={() => { setEditingSite(null); setSiteFormOpen(true); }}
                        className="mt-5 px-4 py-2 text-sm font-bold text-black bg-rb-brand
                                   hover:bg-yellow-400 rounded-xl transition-colors inline-flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Add First Site
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sites.map(site => (
                        <SiteCard
                          key={site.id}
                          site={site}
                          sampleCount={sampleCountForSite(site.id)}
                          isActive={site.id === activeSiteId}
                          onActivate={() => setAndPersistActiveSite(
                            site.id === activeSiteId ? null : site.id
                          )}
                          onEdit={() => handleEditSite(site)}
                          onDelete={() => handleDeleteSite(site.id)}
                          onManageReservoir={() => navigate('/reservoir-management', { state: { site } })}
                        />
                      ))}
                    </div>
                  )}

                  {/* Info note about site tagging */}
                  {sites.length > 0 && (
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
                      <Info className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-slate-500 leading-relaxed">
                        <span className="font-bold text-slate-400">Site filtering:</span> When a site is set as active,
                        the Influent and Effluent tabs filter to samples tagged with that site.
                        Samples added before site assignment (or without a site tag) appear under all sites.
                        New samples created while a site is active will be tagged automatically where supported.
                      </p>
                    </div>
                  )}
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
